/**
 * Connection-aware loading utilities for optimizing image delivery
 * based on user's network conditions and device capabilities
 */

export interface ConnectionInfo {
  effectiveType: '4g' | '3g' | '2g' | 'slow-2g';
  downlink: number;
  saveData: boolean;
}

/**
 * Get network connection information
 */
export function getConnectionInfo(): ConnectionInfo | null {
  const connection = (navigator as any).connection;
  if (!connection) return null;
  
  return {
    effectiveType: connection.effectiveType || '4g',
    downlink: connection.downlink || 10,
    saveData: connection.saveData || false
  };
}

/**
 * Determine optimal image quality based on connection
 */
export function getOptimalQuality(context: 'hero' | 'content' | 'card' | 'small' | 'custom'): number {
  const connection = getConnectionInfo();
  
  if (!connection) {
    // Default qualities when connection info unavailable
    return context === 'hero' ? 60 : context === 'content' ? 65 : 70;
  }

  const baseQuality = context === 'hero' ? 60 : context === 'content' ? 65 : 70;
  
  if (connection.saveData || connection.effectiveType === 'slow-2g') {
    return Math.max(baseQuality - 20, 40);
  }
  
  if (connection.effectiveType === '2g') {
    return Math.max(baseQuality - 15, 45);
  }
  
  if (connection.effectiveType === '3g') {
    return Math.max(baseQuality - 10, 50);
  }
  
  return baseQuality;
}

/**
 * Determine if image should be loaded based on connection
 */
export function shouldLoadImage(priority: 'high' | 'low' = 'low'): boolean {
  const connection = getConnectionInfo();
  
  if (!connection) return true;
  
  if (connection.saveData && priority === 'low') {
    return false;
  }
  
  if (connection.effectiveType === 'slow-2g' && priority === 'low') {
    return false;
  }
  
  return true;
}

/**
 * Get optimal widths array based on connection
 */
export function getConnectionAwareWidths(baseWidths: number[]): number[] {
  const connection = getConnectionInfo();
  
  if (!connection) return baseWidths;
  
  if (connection.saveData || connection.effectiveType === 'slow-2g') {
    // Use fewer, smaller sizes for slow connections
    return baseWidths.slice(0, 2);
  }
  
  if (connection.effectiveType === '2g') {
    return baseWidths.slice(0, 3);
  }
  
  return baseWidths;
}

/**
 * Preload critical images based on connection
 */
export function preloadImage(src: string, width: number, quality: number): void {
  const connection = getConnectionInfo();
  
  // Skip preloading on slow connections or data saver mode
  if (connection && (connection.saveData || connection.effectiveType === 'slow-2g')) {
    return;
  }
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  
  // Add to head and clean up after 5 seconds
  document.head.appendChild(link);
  setTimeout(() => {
    if (document.head.contains(link)) {
      document.head.removeChild(link);
    }
  }, 5000);
}